import { useMemo } from 'react';
import { PendingPhase } from './PendingPhase';
import { Reconciliation, type StepDetail } from './Reconciliation';
import { useAssemblySteps } from './categoryBands';
import { usePartsRun } from './partsRun';
import { useStepParts } from './stepPartsRun';
import { useStepInstructions } from './stepInstructionsRun';
import { usePartCrops } from './partCrop';
import { useLanguage } from '../../contexts/LanguageContext';

// Phase 4's OVERVIEW — WHAT THE STEPS ARE, before what they are made of (the Sub-steps
// subphase beside it). Two calls, one question each, run from the phase's badge (StepsBadge):
//
// THE REPARTITION: how the supplied parts are shared out among the assembly steps. One call
// over the whole manual, because only a reader holding the entire list and every step's
// diagram at once can make it — four legs are supplied, so the steps between them use four.
//
// THE PRINTED TEXT: what each step says, transcribed verbatim, one call per step. It was a
// clause of Phase 4's analysis (see stepInstructionsRun, which argues the move). It belongs
// beside the repartition because it is the same kind of thing — a fact about a step, settled
// before anybody asks what the step is made of — and because Phase 4 can then be given it
// rather than asked for it.
//
// The repartition has been under the parts list and inside the step analysis, on the argument
// that it is a statement about the supplied QUANTITIES and then that it is what the analysis
// reads. Both are true and neither makes it part of another question.
export function StepPartsPanel({ docId, file, locked, held }: {
  docId: string;
  file: Blob;
  locked?: boolean;
  // The run that holds this phase is paused: it is not coming until Reprendre.
  held?: boolean;
}) {
  const { t } = useLanguage();
  const { steps } = useAssemblySteps(docId);
  const { items } = usePartsRun(docId);
  const alloc = useStepParts(docId);
  const said = useStepInstructions(docId);
  const itemCrops = usePartCrops(file, items);

  const allocDone = Object.keys(alloc.byStep).length > 0;
  const canAllocate = items.length > 0 && steps.length > 0;
  const readDone = Object.keys(said.byStep).length > 0;
  // THE TOOLS THIS CALL ANSWERS, drawn where it is made. They are columns of the same table
  // — a mark per step that calls for them, past the parts behind a divider — and until now
  // they were only ever visible in Phase 4, which is a phase that did not ask the question.
  // `undefined` while no step has any, so a manual allocated before the tools were asked for
  // does not grow a band of empty columns.
  //
  // AND WHAT EACH STEP PRINTS, verbatim and per language — the table's Printed text column, once
  // the transcription has answered (an empty list is its answer for a step that prints nothing).
  // It was a list of cards under the table, the same steps a second time.
  const stepDetail = useMemo(() => {
    const out: Record<string, StepDetail> = {};
    for (const s of steps) {
      const tools = alloc.tools[s.category];
      out[s.category] = {
        ...(tools?.length ? { tools } : {}),
        // While it is out too, so the column stands and spins rather than appearing at the end.
        ...(readDone || said.busy ? { instructions: said.busy ? [] : said.byStep[s.category] ?? [] } : {}),
      };
    }
    return Object.values(out).some((d) => d.tools || d.instructions) ? out : undefined;
  }, [steps, alloc.tools, readDone, said.busy, said.byStep]);

  // Under a "Run all" that has not reached this phase, show only the waiting control —
  // never an empty table, and never "this needs a parts list" for a list the run is
  // about to produce.
  if (locked && !allocDone && !alloc.busy) return <PendingPhase held={held} />;

  if (!canAllocate) {
    return (
      <p className="text-center text-sm text-slate-400">
        {t('notice.parts.stepsNeeds')}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {(alloc.error || said.error) && (
        <p className="text-center text-sm text-rose-600">
          {alloc.error || said.error}
        </p>
      )}

      {/* THE TABLE STAYS WHILE ITS CALLS ARE OUT, and every cell they answer spins: the
          counts, the step's line and its tools while the repartition runs, the printed text while
          the transcription does. The last run's numbers are never shown as this run's — and the
          table no longer vanishes under one spinner, which left nothing to watch fill in. */}
      {(allocDone || alloc.busy || said.busy) && (
        <Reconciliation
          items={items}
          itemCrops={itemCrops}
          used={alloc.byStep}
          steps={steps}
          stepText={alloc.text}
          detail={stepDetail}
          allocBusy={alloc.busy}
          printedBusy={said.busy}
        />
      )}
    </div>
  );
}
